import * as React from 'react'

import { KonvaEventObject } from 'konva/lib/Node'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import SitemapElementsWrapper from '~/client/src/shared/components/SitemapElementsWrapper'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import ICanvasImageCache from '~/client/src/shared/interfaces/ITextboxesCache'
import Awaiter from '~/client/src/shared/utils/Awaiter'

import KonvaBoundingBox from './drawnParts/KonvaBoundingBox'
import KonvaSitemapIcon from './drawnParts/KonvaSitemapIcon'
import KonvaSitemapLabel from './drawnParts/KonvaSitemapLabel'
import KonvaSitemapShape from './drawnParts/KonvaSitemapShape'

export const MAX_PERCENT = 100

interface IProps {
  items: MapViewItemBase[]
  containerWidth: number
  containerHeight: number
  onSelectItem?: (item: MapViewItemBase, top?: number, left?: number) => void
  isEditMode?: boolean
  textboxesAwaiter?: Awaiter
  textboxesCache?: ICanvasImageCache

  className?: string
  isReferenced?: boolean

  isDisabled?: boolean
  selectedStartDate?: Date
  selectedEndDate?: Date
}

@observer
export default class KonvaSitemapItems extends React.Component<IProps> {
  @observable private boundingBoxItem: MapViewItemBase = null

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  public render() {
    const { items, className, isDisabled } = this.props
    return (
      <SitemapElementsWrapper className={className}>
        {!isDisabled && this.renderBoundingBox()}
        {items.map(this.renderShape)}
        {items.map(this.renderLabel)}
        {items.map(this.renderIcon)}
      </SitemapElementsWrapper>
    )
  }

  private renderIcon = (item: MapViewItemBase, idx: number) => {
    const {
      containerHeight,
      containerWidth,
      selectedStartDate,
      selectedEndDate,
    } = this.props

    return (
      <KonvaSitemapIcon
        key={`${idx}-${item.id}`}
        item={item}
        containerHeight={containerHeight}
        containerWidth={containerWidth}
        onClick={this.selectItem.bind(this, item)}
        onMouseEnter={this.showBoundingBox.bind(this, item)}
        onMouseLeave={this.hideBoundingBox}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
      />
    )
  }

  private renderLabel = (item: MapViewItemBase, idx: number) => {
    const {
      containerHeight,
      containerWidth,
      textboxesAwaiter,
      textboxesCache,
      isReferenced,
    } = this.props

    return (
      <KonvaSitemapLabel
        key={`${idx}-${item.id}`}
        item={item}
        containerHeight={containerHeight}
        containerWidth={containerWidth}
        onClick={this.selectItem.bind(this, item)}
        onMouseEnter={this.showBoundingBox.bind(this, item)}
        onMouseLeave={this.hideBoundingBox}
        textboxesAwaiter={textboxesAwaiter}
        textboxesCache={textboxesCache}
        isReferenced={isReferenced}
      />
    )
  }

  private renderShape = (item: MapViewItemBase, idx: number) => {
    const { containerHeight, containerWidth } = this.props

    return (
      <KonvaSitemapShape
        key={`${idx}-${item.id}`}
        item={item}
        containerHeight={containerHeight}
        containerWidth={containerWidth}
        onClick={this.selectItem.bind(this, item)}
        onMouseEnter={this.showBoundingBox.bind(this, item)}
        onMouseLeave={this.hideBoundingBox}
        isStaticSize={true}
      />
    )
  }

  private selectItem(
    item: MapViewItemBase,
    event: KonvaEventObject<MouseEvent>,
  ) {
    const { onSelectItem, isDisabled } = this.props
    const { iconProperties, labelProperties } = item.sitemapItemProperties
    if (!onSelectItem || isDisabled) {
      return
    }

    // for Line type (shape properties only)
    if (!iconProperties && !labelProperties) {
      event.cancelBubble = true
      onSelectItem(item)
      return
    }

    event.cancelBubble = true
    onSelectItem(item, event.evt.layerY, event.evt.layerX)
  }

  private renderBoundingBox() {
    const { containerHeight, containerWidth, items, isEditMode } = this.props

    const baseItem = items.find(i => i.id === this.boundingBoxItem?.id)

    if (
      !isEditMode ||
      !this.boundingBoxItem ||
      !items.includes(this.boundingBoxItem) ||
      !baseItem
    ) {
      return null
    }

    const boundingBox =
      this.boundingBoxItem.sitemapItemProperties.getBoundingBox(
        containerWidth,
        containerHeight,
        this.boundingBoxItem.iconName,
        this.boundingBoxItem.name,
      )

    return <KonvaBoundingBox boundingBox={boundingBox} isStaticSize={true} />
  }

  @action
  private showBoundingBox(item: MapViewItemBase) {
    this.boundingBoxItem = item
  }

  @action
  private hideBoundingBox = () => {
    this.boundingBoxItem = null
  }
}
